import React from 'react';
import { PropTypes } from '../../../utils/propTypes';
import { Icon } from '../Icon';
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from './Empty';
import styles from './AnalysisEmpty.module.css';

/**
 * AnalysisEmpty — the one consistent empty state for every
 * thread-dependent workspace tab (Reports, Workflow Versions,
 * Compliance Mapping, Residual TARA, Attack Trees, Traceability,
 * Traceability Map).
 *
 * A workspace tab has a `thread_id` only once the TARA workflow has
 * started (a document was uploaded and "Start Analysis" was clicked).
 * Before that, these tabs have nothing to show — they previously each
 * rendered a differently-styled placeholder (document icon, shield
 * icon, bare text notice, an `<h2>No Thread ID</h2>`, …). This wraps
 * the shared `Empty` primitive (already the look Residual TARA uses)
 * in a height-filling, vertically-centred container so every tab reads
 * identically.
 *
 * Defaults describe the canonical "TARA not started" case. Override
 * `icon` / `title` / `description` (and pass `children` for an action)
 * to reuse the same chassis for a tab's data-empty state.
 */
const AnalysisEmpty = ({
  icon = 'document',
  title = 'Analysis not started yet',
  description = "This project hasn't started a TARA analysis. Upload a document and start the workflow to populate this tab.",
  children,
  className = '',
}) => (
  <div className={`${styles.fill} ${className}`}>
    <Empty>
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Icon name={icon} size={20} />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        {description ? <EmptyDescription>{description}</EmptyDescription> : null}
      </EmptyHeader>
      {children ? <EmptyContent>{children}</EmptyContent> : null}
    </Empty>
  </div>
);

AnalysisEmpty.propTypes = {
  icon: PropTypes.string,
  title: PropTypes.node,
  description: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default AnalysisEmpty;
